import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { TableRowSelection } from 'antd/es/table/interface';
import {
    Chip,
    DataTable,
    DropdownMenu,
    StatusDot,
    type ColumnConfig,
    type DropdownMenuItem,
    type FilterField,
    type SortInfo,
    type StatusConfig,
} from '@/components/ui';
import { ROLE_MAP_COLUMNS, type RoleMapGroup, type RoleMapSortField } from './types';
import type { RoleMapTableVM } from './useRoleMapTable';
import styles from './RoleMapTable.module.css';
import shared from './roleMapShared.module.css';

function rowActionItems(vm: RoleMapTableVM, group: RoleMapGroup, t: TFunction): DropdownMenuItem[] {
    const items: DropdownMenuItem[] = [];
    if (vm.bulkEnabled) {
        items.push({
            key: 'resync',
            label: t('console.roleMap.table.actionResync'),
            onSelect: () => vm.resyncGroup(group),
        });
        items.push({
            key: 'to-default',
            label: t('console.roleMap.table.actionToDefault'),
            onSelect: () => vm.setGroupScope(group, 'default'),
        });
    }
    // EAA-1817: "Đổi vai trò" re-points via `PATCH /role-maps/{id}` — that endpoint
    // is gated on `ROLEMAP_MANAGE` (`@require_permissions_for_method`, not a
    // superadmin check; see `useRoleMapWrites.repointMutation`'s docstring), and the
    // server asks for that same permission whether the row is default- or org-scoped.
    // So this is gated on `canManageDefault` regardless of the current scope, not
    // `canManageCurrentScope`. Restricted to single-id groups: a multi-role
    // mapping has no single unambiguous id to re-point without first asking
    // WHICH chip, which this row action does not model.
    if (vm.canManageDefault && group.ids.length === 1) {
        items.push({
            key: 'repoint',
            label: t('console.roleMap.table.actionRepoint'),
            onSelect: () => vm.openRepoint(group),
        });
    }
    // EAA-1817: the +/- diff against the ecosystem default only means anything
    // while looking at an org's own override slice.
    if (vm.scope === 'org' && vm.actingOrgId) {
        items.push({
            key: 'scope-diff',
            label: t('console.roleMap.table.actionScopeDiff'),
            onSelect: () => vm.openScopeDiff(group),
        });
    }
    items.push({
        key: 'delete',
        label:
            group.ids.length > 1
                ? t('console.roleMap.table.actionDeleteMany', { count: group.ids.length })
                : t('console.roleMap.table.actionDeleteOne'),
        danger: true,
        disabled: !vm.canManageCurrentScope,
        onSelect: () => vm.removeGroup(group),
    });
    return items;
}

/**
 * EAA-1837/EAA-1817: the flat "Liên kết vai trò" table — design frames 56–59,
 * with frame 62 ("Bảng dữ liệu — đủ bộ điều khiển") as the reference for which
 * controls a standard console table carries.
 *
 * ONE ROW PER `(role_key, application)` GROUP — a multi-role mapping renders
 * its two-plus chips in the single "Vai trò trong ứng dụng" cell, and its
 * checkbox selects every underlying `RoleMap` id (see `RoleMapGroup`'s
 * docstring for the EAA-1828 bug class that makes this non-negotiable).
 *
 * The checkbox column is DISABLED whenever `vm.bulkEnabled` is false — in the
 * "Mặc định" scope no bulk endpoint can touch the rows at all, so offering a
 * selection that could only ever produce a `skipped` response would be a lie.
 *
 * EVERYTHING AROUND THE ROWS IS NOW THE KIT'S (EAA-1817, 2026-08-24). This
 * screen used to bring its own toolbar (`RoleMapToolbar` + `RoleMapFilterButton`
 * — a search box, three filter buttons with hand-built checkbox menus, a
 * "Xoá bộ lọc" link and a "Cột" picker), its own pager (`RoleMapPagination`,
 * including a native `<select>` for the page size) and its own sort/filter
 * header cells with hand-drawn carets and funnels. All four are deleted: they
 * are `DataTable`'s `showToolbar`, `pagination`, `ColumnConfig.sorter` and
 * `ColumnConfig.filter` respectively.
 *
 * The previous version's comment argued the opposite — that the kit's
 * mechanisms "would fight this screen's actual design", because the sort cycle
 * ran through `vm.toggleSort` and the header funnel was a second trigger for
 * the toolbar's own popover. That reasoning does not survive contact with frame
 * 62: the standard table's header caret and funnel ARE `sorter` and `filter`,
 * and the toolbar button per filter field is what `columnDrivenFilterFields`
 * renders. What actually had to move was small — an ordering-string adapter for
 * the sort (below) and a set-style `setFilterValue` on the hook — and in
 * exchange the screen stops maintaining a fifth copy of a filter popover.
 */
export function RoleMapTable({ vm }: { vm: RoleMapTableVM }) {
    const { t } = useTranslation();
    const showApplication = vm.isColumnVisible('application');
    const showAppRoles = vm.isColumnVisible('app_roles');
    const showScope = vm.isColumnVisible('scope');
    const showStatus = vm.isColumnVisible('status');
    // EAA-1817: caller-owned config map for the shared `StatusDot` primitive —
    // same labels/tones the old `.role-map-status.is-ok|is-stale` classes rendered.
    const statusConfig = useMemo<Record<'ok' | 'stale', StatusConfig>>(
        () => ({
            ok: { label: t('console.roleMap.status.normal'), tone: 'success' },
            stale: { label: t('console.roleMap.status.stale'), tone: 'error' },
        }),
        [t]
    );

    /** Frame 56's own filter buttons: "Tình trạng", "Ứng dụng", "Phạm vi". */
    const appFilterField = useMemo<Omit<FilterField, 'key' | 'columnKey'>>(
        () => ({
            label: t('console.roleMap.toolbar.appFilterLabel'),
            type: 'multiSelect',
            showSearch: true,
            options: vm.apps.map((app) => ({
                label: `${app.code} · ${app.name}`,
                value: app.code,
            })),
        }),
        [t, vm.apps]
    );

    const columns = useMemo<ColumnConfig<RoleMapGroup>[]>(() => {
        const cols: ColumnConfig<RoleMapGroup>[] = [
            {
                key: 'role_key',
                title: t('console.roleMap.table.columnRoleKey'),
                sorter: true,
                hideable: false,
                render: (_value, group) => <code className={shared.roleKey}>{group.role_key}</code>,
            },
        ];

        if (showApplication) {
            cols.push({
                key: 'application',
                title: t('console.roleMap.table.columnApplication'),
                sorter: true,
                filter: appFilterField,
                render: (_value, group) => (
                    <span className={styles.appCell}>
                        {/* The registry code can be longer than the 26px tile;
                            the tile shows the first three characters and the
                            full code stays in the tooltip. */}
                        <span className={styles.appCellTile} title={group.application}>
                            {group.application.slice(0, 3)}
                        </span>
                        {group.appName}
                    </span>
                ),
            });
        }

        if (showAppRoles) {
            cols.push({
                key: 'app_roles',
                title: t('console.roleMap.table.columnAppRoles'),
                render: (_value, group) => (
                    <span className={styles.appRoles}>
                        {/* EAA-1817: the chip PRINTS the app role's name and keeps
                            its key in the tooltip — the design's 2026-08-24 pass
                            ("dùng nhiều mã quá"). `mono={false}` with it: a
                            Vietnamese phrase set in monospace reads as a code
                            that happens to have spaces in it. */}
                        {group.appRoles.map((chip) => (
                            <Chip
                                key={chip.id}
                                tone={chip.stale ? 'stale' : 'mapped'}
                                mono={false}
                                title={t('console.roleMap.table.appRoleCodeTooltip', {
                                    code: chip.app_role,
                                })}
                            >
                                {chip.app_role_name}
                            </Chip>
                        ))}
                    </span>
                ),
            });
        }

        cols.push({
            key: 'accounts',
            title: t('console.roleMap.table.columnAccounts'),
            sorter: true,
            render: (_value, group) => (
                // EAA-1884: `.role-map-accounts-cell` only sets `font-variant-numeric` —
                // a span carries it fine now that `DataTable` owns the `<td>`.
                <span className={styles.accountsCell}>{group.accountCount}</span>
            ),
        });

        if (showScope) {
            cols.push({
                key: 'scope',
                title: t('console.roleMap.table.columnScope'),
                filter: {
                    label: t('console.roleMap.toolbar.scopeFilterLabel'),
                    type: 'select',
                    options: [
                        ...(vm.canManageDefault
                            ? [
                                  {
                                      label: t('console.roleMap.toolbar.scopeDefaultLabel'),
                                      value: 'default',
                                  },
                              ]
                            : []),
                        {
                            label:
                                vm.actingOrgName ?? t('console.roleMap.toolbar.scopeOrgFallback'),
                            value: 'org',
                        },
                    ],
                },
                render: (_value, group) => (
                    <span className={styles.scopeCell}>
                        {group.organization_id === null
                            ? t('console.roleMap.table.scopeDefault')
                            : (vm.actingOrgName ?? t('console.roleMap.table.scopeOrgFallback'))}
                    </span>
                ),
            });
        }

        if (showStatus) {
            cols.push({
                key: 'status',
                title: t('console.roleMap.table.columnStatus'),
                filter: {
                    label: t('console.roleMap.toolbar.statusFilterLabel'),
                    type: 'multiSelect',
                    options: [
                        { label: t('console.roleMap.status.normal'), value: 'normal' },
                        { label: t('console.roleMap.status.stale'), value: 'stale' },
                    ],
                },
                render: (_value, group) => (
                    <StatusDot
                        className={styles.status}
                        status={group.isStale ? 'stale' : 'ok'}
                        config={statusConfig}
                    />
                ),
            });
        }

        return cols;
    }, [
        t,
        showApplication,
        showAppRoles,
        showScope,
        showStatus,
        statusConfig,
        appFilterField,
        vm.canManageDefault,
        vm.actingOrgName,
    ]);

    /**
     * EAA-1884: header checkbox state/indeterminate/disabled, and the
     * "select all" click cycle, are entirely antd's — `getCheckboxProps`
     * disabling every row the same way the old per-row `disabled` prop did is
     * what makes antd auto-disable the header checkbox too (its own
     * `useSelection` hook disables "select all" when every row on the page is
     * individually disabled, or the page is empty), which is exactly
     * `!vm.bulkEnabled || vm.pageGroups.length === 0` without re-deriving it.
     */
    const rowSelection = useMemo<TableRowSelection<RoleMapGroup>>(
        () => ({
            selectedRowKeys: Array.from(vm.selectedKeys),
            onChange: vm.onSelectionChange,
            getCheckboxProps: (record) => ({
                disabled: !vm.bulkEnabled,
                'aria-label': t('console.roleMap.table.selectRowAria', {
                    roleKey: record.role_key,
                    appName: record.appName,
                }),
            }),
            getTitleCheckboxProps: () => ({
                'aria-label': t('console.roleMap.table.selectAllAria'),
            }),
        }),
        [vm.selectedKeys, vm.onSelectionChange, vm.bulkEnabled, t]
    );

    const rowClassName = useCallback(
        (group: RoleMapGroup) => {
            const selected = vm.selectedKeys.has(group.key);
            // Stale wins over selected on purpose — the export's librarian row
            // is both, and renders the danger tone (see 14-role-map.css).
            return ((selected ? 'is-selected' : '') + (group.isStale ? ' is-stale' : '')).trim();
        },
        [vm.selectedKeys]
    );

    /**
     * EAA-1817: `DataTable` reports sort as an ordering STRING (`role_key` /
     * `-role_key`, antd's own convention once a `sortKey` is resolved) because
     * that is what a server-paged list sends upstream. This table sorts in
     * memory, so the string is parsed straight back into the hook's
     * field/direction pair. Clearing the sort (antd's third click) falls back
     * to the default `role_key` ascending rather than an unordered table: the
     * rows are grouped pairs, and an arbitrary order makes a scan for one
     * `role_key` impossible.
     */
    const sortedInfo = useMemo<SortInfo>(
        () => ({
            field: vm.sortField,
            order: vm.sortDirection === 'asc' ? 'ascend' : 'descend',
        }),
        [vm.sortField, vm.sortDirection]
    );

    const handleSortChange = useCallback(
        (ordering: string) => {
            const descending = ordering.startsWith('-');
            const field = (descending ? ordering.slice(1) : ordering) as RoleMapSortField;
            vm.setSort(field || 'role_key', !ordering || !descending ? 'asc' : 'desc');
        },
        [vm]
    );

    const visibleColumnKeys = useMemo(
        () =>
            ROLE_MAP_COLUMNS.filter((column) => column.pinned || vm.isColumnVisible(column.key))
                .map((column) => column.key as string)
                // `accounts` is not part of the configurable set but must stay
                // visible; `DataTable`'s picker works off the keys it is handed.
                .concat('accounts'),
        [vm]
    );

    return (
        <div className="portal-table-scroll">
            <DataTable<RoleMapGroup>
                className="role-map-datatable"
                data={vm.pageGroups}
                columns={columns}
                rowKey="key"
                showToolbar
                searchValue={vm.search}
                searchPlaceholder={t('console.roleMap.toolbar.searchPlaceholder')}
                onSearchChange={vm.setSearch}
                filterValues={vm.filterValues}
                onFilterChange={vm.setFilterValue}
                onReset={vm.clearFilters}
                visibleColumns={visibleColumnKeys}
                onVisibleColumnsChange={vm.setVisibleColumnKeys}
                toolbarExtra={
                    <span className={styles.toolbarCount}>
                        {t('console.roleMap.toolbar.showingCount', {
                            shown: vm.filteredCount,
                            total: vm.totalLinks,
                        })}
                    </span>
                }
                pagination={{
                    page: vm.page,
                    pageSize: vm.pageSize,
                    total: vm.filteredCount,
                }}
                onPageChange={(page, pageSize) => {
                    if (pageSize !== vm.pageSize) vm.setPageSize(pageSize);
                    else vm.setPage(page);
                }}
                showSizeChanger
                pageSizeOptions={vm.pageSizeOptions.map(String)}
                sortedInfo={sortedInfo}
                onSortChange={handleSortChange}
                rowSelection={rowSelection}
                compactSelectionColumn
                rowClassName={rowClassName}
                actions={(group) => (
                    <DropdownMenu
                        items={rowActionItems(vm, group, t)}
                        triggerAriaLabel={t('console.roleMap.table.rowActionsAria', {
                            roleKey: group.role_key,
                            appName: group.appName,
                        })}
                    />
                )}
                actionsWidth={44}
            />
        </div>
    );
}

export default RoleMapTable;
